import React from 'react';
import { Box, Container, Card, CardContent, Typography, Rating, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { ScrollReveal } from '../../animations';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * Testimonials/Reviews Section
 */

const TestimonialCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.custom.borderRadius.lg,
  boxShadow: theme.custom.boxShadow.sm,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    boxShadow: theme.custom.boxShadow.lg,
    transform: 'translateY(-4px)',
  },
}));

const testimonials = [
  {
    name: 'Muhammad Ahmed',
    role: 'Dubai, UAE',
    avatar: 'https://i.pravatar.cc/150?img=1',
    rating: 5,
    text: 'Excellent service! The entire booking process was smooth and hassle-free. The package included everything as promised.',
  },
  {
    name: 'Fatima Hassan',
    role: 'London, UK',
    avatar: 'https://i.pravatar.cc/150?img=2',
    rating: 5,
    text: 'Best Umrah experience ever! The hotels were comfortable, guides were knowledgeable, and customer support was amazing.',
  },
  {
    name: 'Ali Khan',
    role: 'Toronto, Canada',
    avatar: 'https://i.pravatar.cc/150?img=3',
    rating: 5,
    text: 'Highly recommended! The pricing is competitive and they offer flexible payment options. Worth every penny!',
  },
  {
    name: 'Aisha Malik',
    role: 'Sydney, Australia',
    avatar: 'https://i.pravatar.cc/150?img=4',
    rating: 5,
    text: 'Professional team with great attention to detail. They made our Umrah journey unforgettable.',
  },
  {
    name: 'Hassan Ibrahim',
    role: 'Jeddah, Saudi Arabia',
    avatar: 'https://i.pravatar.cc/150?img=5',
    rating: 5,
    text: 'Fast booking, friendly staff, and excellent accommodation. Truly a 5-star experience!',
  },
];

const TestimonialsSection = () => {
  return (
    <Box sx={{ py: 10, backgroundColor: 'white' }}>
      <Container maxWidth="xl">
        {/* Section Header */}
        <ScrollReveal animation="fadeInUp">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'primary.main',
              }}
            >
              What Our Customers Say
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Join travelers who book Umrah, ziyarat, and holidays with us
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Testimonials Carousel */}
        <ScrollReveal animation="fadeInUp" delay={0.2}>
          <Box sx={{ position: 'relative' }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                600: { slidesPerView: 2 },
                960: { slidesPerView: 3 },
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard>
                    <CardContent sx={{ p: 3 }}>
                      {/* Rating */}
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        sx={{ mb: 2 }}
                      />

                      {/* Text */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 3,
                          fontStyle: 'italic',
                          lineHeight: 1.7,
                        }}
                      >
                        "{testimonial.text}"
                      </Typography>

                      {/* Author */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <Avatar
                          src={testimonial.avatar}
                          sx={{ width: 44, height: 44 }}
                        />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </TestimonialCard>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </ScrollReveal>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
